/**
 * OperatorAssignPicker
 * Dropdown per assegnare la chat a un operatore del team
 */

import { useState, useRef, useEffect } from 'react';
import { UserPlus, X, ChevronDown } from 'lucide-react';
import type { OrgMember } from '@/lib/stores/whatsappChatMetaStore';
import { tip } from '@/components/messages/ChatTooltip';

interface Props {
  assignedOperatorId: string | null;
  members: OrgMember[];
  currentUserId?: string;
  onAssign: (userId: string | null) => void;
}

export function OperatorAssignPicker({ assignedOperatorId, members, onAssign }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const assigned = members.find((m) => m.id === assignedOperatorId);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initials = (name: string) => name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-[rgba(55,53,47,0.12)] dark:border-white/10 bg-white dark:bg-gray-800 text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors"
        {...tip('Assegna operatore')}
      >
        {assigned ? (
          <>
            <span className="w-4 h-4 rounded-full bg-[#2383E2] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
              {initials(assigned.full_name)}
            </span>
            <span className="max-w-[80px] truncate">{assigned.full_name.split(' ')[0]}</span>
            <ChevronDown className="w-3 h-3 opacity-60" />
          </>
        ) : (
          <>
            <UserPlus className="w-3.5 h-3.5 text-[#787774]" />
            <span className="text-[#787774]">Assegna</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 py-1 min-w-[180px] max-h-64 overflow-y-auto">
          {assigned && (
            <button
              onClick={() => { onAssign(null); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Rimuovi assegnazione
            </button>
          )}
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => { onAssign(m.id); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 transition-colors ${m.id === assignedOperatorId ? 'bg-[rgba(35,131,226,0.08)]' : ''}`}
            >
              <span className="w-6 h-6 rounded-full bg-[#2383E2] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                {initials(m.full_name)}
              </span>
              <div className="min-w-0">
                <div className="text-[#37352F] dark:text-white font-medium truncate">{m.full_name}</div>
                <div className="text-[#787774] truncate">{m.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
